/**
 * NotificationsBell — bell icon + unread badge + dropdown with the user's
 * in-app notifications. Lives in the AppShell header / sidebar.
 *
 * Read state is per-notification: clicking an item navigates to the
 * action URL and marks that row read. "Mark all read" clears the badge.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import {
  useMarkAllRead,
  useMarkNotificationRead,
  useMyNotifications,
  type NotificationStoredPayload,
} from '@/api/my-notifications';

export function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useMyNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllRead();

  const unread = data?.unread_count ?? 0;
  const items = data?.data ?? [];

  function handleItemClick(id: string, actionUrl: string | null) {
    markRead.mutate(id);
    if (actionUrl) {
      // Same-origin → push state via the router. Off-origin → window.location.
      try {
        const url = new URL(actionUrl, window.location.origin);
        if (url.origin === window.location.origin) {
          navigate(url.pathname + url.search + url.hash);
          setOpen(false);
          return;
        }
      } catch {
        // fall through
      }
      window.location.href = actionUrl;
    }
    setOpen(false);
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          className="relative flex items-center justify-center w-7 h-7 rounded text-ink-dim hover:text-ink hover:bg-fill-soft"
          aria-label={
            unread > 0
              ? `${unread} unread notification${unread === 1 ? '' : 's'}`
              : 'Notifications'
          }
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-red-600 text-white text-[9px] font-semibold leading-[14px] text-center">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
      <Popover.Content
        side="bottom"
        align="end"
        sideOffset={6}
        className="z-50 w-[360px] rounded-md border border-line bg-bg shadow-lg outline-none"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-line">
          <div className="text-sm font-semibold">Notifications</div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAll.mutate()}
              title="Mark all read"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {isLoading ? (
            <div className="p-6 text-xs text-ink-faint">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-xs text-ink-faint">
              No notifications yet.
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {items.map((n) => {
                const p = n.payload as unknown as NotificationStoredPayload;
                const isUnread = n.status === 'sent';
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => handleItemClick(n.id, p.action_url)}
                      className={cn(
                        'w-full text-left px-3 py-2.5 hover:bg-fill-soft transition-colors',
                        isUnread && 'bg-fill-soft/50',
                      )}
                    >
                      <div className="flex items-start gap-2">
                        {isUnread && (
                          <span className="w-1.5 h-1.5 rounded-full bg-sky-600 mt-1.5 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div
                            className={cn(
                              'text-xs',
                              isUnread ? 'font-semibold' : 'font-normal text-ink-dim',
                            )}
                          >
                            {p.subject ?? p.event}
                          </div>
                          <div className="text-[11px] text-ink-dim mt-0.5 line-clamp-2">
                            {p.short}
                          </div>
                          <div className="text-[10px] text-ink-faint mt-1">
                            {formatRelative(n.queued_at)}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
